import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as PosterIcon, Upload, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { AuthedImage, fmtDate } from "@/components/posters/posterUtils";
import { AdminPosterTemplateEditor } from "@/components/posters/AdminPosterTemplateEditor";

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50";
const STATUS_CLS = { published: "bg-emerald-50 text-emerald-800 border-emerald-200", draft: "bg-amber-50 text-amber-800 border-amber-200", archived: "bg-stone-100 text-stone-600 border-stone-200" };

function UploadForm({ onCreated }) {
  const [f, setF] = useState({ title: "", description: "", default_description: "", file: null });
  const [busy, setBusy] = useState(false);
  const input = "w-full px-3 py-2 bg-white border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-stone-900";
  const submit = async (e) => {
    e.preventDefault();
    if (!f.file || !f.title.trim()) { toast.error("Add a title and choose a PDF."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("pdf", f.file); fd.append("title", f.title); fd.append("description", f.description); fd.append("default_description", f.default_description);
      const { data } = await api.post("/admin/poster-templates", fd);
      toast.success("Template uploaded as a draft"); setF({ title: "", description: "", default_description: "", file: null }); e.target.reset(); onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-stone-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="poster-template-upload">
      <h2 className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Upload a new template</h2>
      <input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} data-testid="poster-upload-title" />
      <input placeholder="Description (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={input} data-testid="poster-upload-description" />
      <textarea rows={2} placeholder="Default project wording" value={f.default_description} onChange={(e) => setF({ ...f, default_description: e.target.value })} className={`${input} md:col-span-2`} data-testid="poster-upload-default" />
      <input type="file" accept="application/pdf" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} className="text-xs" data-testid="poster-upload-file" />
      <div className="flex items-center justify-end gap-3">
        <span className="text-[11px] text-stone-500">One-page portrait A4 PDF with the text areas left blank. Max 25 MB.</span>
        <button type="submit" disabled={busy} className={`${btn} bg-stone-950 text-[#dddd16] hover:bg-stone-800`} data-testid="poster-upload-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
        </button>
      </div>
    </form>
  );
}

function VersionRow({ t, onChanged, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const act = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(""); }
  };
  const publish = () => act("publish", async () => { await api.post(`/admin/poster-templates/${t.id}/publish`); toast.success("Published"); onChanged(); });
  const archive = () => act("archive", async () => { await api.post(`/admin/poster-templates/${t.id}/archive`); toast.success("Archived"); onChanged(); });
  const del = () => act("delete", async () => { await api.delete(`/admin/poster-templates/${t.id}`); toast.success("Draft deleted"); onRemoved(); });
  const newVersion = (file) => act("version", async () => {
    const fd = new FormData(); if (file) fd.append("pdf", file);
    await api.post(`/admin/poster-templates/${t.id}/versions`, fd); toast.success("New draft version created"); onChanged();
  });
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`poster-template-row-${t.id}`}>
      <div className="flex flex-wrap items-center gap-4">
        <AuthedImage path={`/admin/poster-templates/${t.id}/thumbnail.jpg`} alt={t.title} className="w-14 h-20 object-cover rounded border border-stone-100" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-stone-900">{t.title}</span>
            <span className="text-[11px] text-stone-500">v{t.version}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_CLS[t.status]}`} data-testid={`poster-template-status-${t.id}`}>{t.status}</span>
          </div>
          <div className="text-[11px] text-stone-500">{t.poster_count} saved poster{t.poster_count === 1 ? "" : "s"} · updated {fmtDate(t.updated_at)}</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {t.status !== "published" && <button type="button" onClick={publish} disabled={!!busy} className={`${btn} bg-stone-950 text-[#dddd16] hover:bg-stone-800`} data-testid={`poster-template-publish-${t.id}`}>Publish</button>}
          {t.status !== "archived" && <button type="button" onClick={archive} disabled={!!busy} className={`${btn} border border-stone-300 text-stone-700 hover:border-stone-950`} data-testid={`poster-template-archive-${t.id}`}>Archive</button>}
          <label className={`${btn} border border-stone-300 text-stone-700 hover:border-stone-950 cursor-pointer`} data-testid={`poster-template-new-version-${t.id}`}>
            New version (new PDF)
            <input type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && newVersion(e.target.files[0])} />
          </label>
          <button type="button" onClick={() => newVersion(null)} disabled={!!busy} className={`${btn} border border-stone-300 text-stone-700 hover:border-stone-950`} data-testid={`poster-template-copy-version-${t.id}`}>New version (same PDF)</button>
          {t.status === "draft" && t.poster_count === 0 && <button type="button" onClick={del} disabled={!!busy} className={`${btn} border border-stone-300 text-red-700 hover:border-red-600`} data-testid={`poster-template-delete-${t.id}`}>Delete draft</button>}
          <button type="button" onClick={() => setOpen((o) => !o)} className={`${btn} text-stone-700 hover:text-stone-950`} data-testid={`poster-template-toggle-${t.id}`}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {open ? "Close" : "Edit"}
          </button>
        </div>
      </div>
      {open && <AdminPosterTemplateEditor template={t} onSaved={onChanged} />}
    </div>
  );
}

export default function AdminPosterTemplatesPage() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get("/admin/poster-templates").then(({ data }) => setItems(data.items || [])).catch((e) => setError(e?.response?.data?.detail || "Could not load poster templates."));
  useEffect(() => { load(); }, []);
  return (
    <div className="px-6 md:px-10 py-8 max-w-6xl mx-auto space-y-6" data-testid="admin-poster-templates-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 flex items-center gap-1.5"><PosterIcon className="w-3 h-3" /> Poster Studio</div>
        <h1 className="font-display text-4xl font-black text-stone-950 mt-1">Poster Templates</h1>
        <p className="text-sm text-stone-600 mt-1">Published templates are available to every franchisee in Poster Studio. Saved posters keep the exact version they were made with.</p>
      </div>
      <UploadForm onCreated={load} />
      {error && <div className="text-sm text-red-700">{error}</div>}
      {!items && !error && <Loader2 className="w-5 h-5 animate-spin text-stone-400" />}
      <div className="space-y-3">{(items || []).map((t) => <VersionRow key={t.id} t={t} onChanged={load} onRemoved={load} />)}</div>
    </div>
  );
}
